import { useEffect, useMemo } from 'react';
import styled from '@emotion/styled';

import { useFormik } from 'formik';
import { AwardingBodyIdentifiers, CustomerJourneyStep, useCustomerJourneyStep, MALAYSIA_COUNTRY_ID } from 'core';
import { CandidateProductDetails as CandidateProductDetailsModel } from 'store/candidate/types';
import { FormFooter, BcCheckbox, ReadonlyField } from 'components';
import { selectCandidate } from 'store/candidate/candidateSlice';
import { selectSelectedCountryId } from 'store/country/countrySlice';
import { useAppSelector } from 'store';
import { useGetIdTypesByCountryQuery } from 'store/idType/idTypeAPI';
import { useLazyExportedRegistrationsCheckQuery } from 'store/candidate/candidateAPI';

import { useFormFields, useServerErrors } from '@britishcouncil/react-forms';
import { MainContainer, PageContainer, Quote, TextField, Divider } from '@britishcouncil/react-solas-ors3';
import { Button, LoadingContainer, BcAlert } from '@britishcouncil/react-solas';

import { CandidateExams, GroupAwards, IdentityDocumentFields } from './components';
import { getValidation } from './validationSchema';
import { useCandidateProductDetailsInitialValues } from './useCandidateProductDetailsInitialValues';
import { useCandidateProductDetailsSubmit } from './useCandidateProductDetailsSubmit';
import { useGetTaxRateQuery } from 'store/organisation-country/organisation-country-API';
import { MalaysianCitizenStatus } from 'core/const/MalaysianCitizenStatus';
import { OnlinePaymentDiscountAdvertMsg } from 'components/OnlinePaymentDiscountAdvertMsg';

export const CandidateProductDetails = () => {
  const { goBack } = useCustomerJourneyStep(CustomerJourneyStep.CandidateProductDetails);
  const countryId = useAppSelector(selectSelectedCountryId);
  const { data: idTypes } = useGetIdTypesByCountryQuery(countryId);
  const candidateDetails = useAppSelector(selectCandidate);
  const {
    initialValues,
    exams,
    isLoading: isCandidateProductDetailsLoading,
    awardingBodyId,
    groupAwardItems,
    eligibilityCheck,
  } = useCandidateProductDetailsInitialValues(countryId);

  const [
    getExportedRegistrationsCheck,
    { data: exportedRegistrationsCheck, isFetching: exportedRegistrationsCheckLoading },
  ] = useLazyExportedRegistrationsCheckQuery();
  const { submit, isSubmitting, serverErrors } = useCandidateProductDetailsSubmit();
  const { data: taxRateData } = useGetTaxRateQuery({ countryId });

  const isLoading = useMemo(
    () => exportedRegistrationsCheckLoading || isCandidateProductDetailsLoading,
    [exportedRegistrationsCheckLoading, isCandidateProductDetailsLoading],
  );

  const canEditDocument = useMemo(
    () => !candidateDetails?.id || !exportedRegistrationsCheck?.hasExportedRegistrationsInCurrentSessions,
    [candidateDetails, exportedRegistrationsCheck],
  );

  const canEditUci = useMemo(
    () =>
      !candidateDetails?.id ||
      (awardingBodyId === AwardingBodyIdentifiers.Edexcel &&
        !exportedRegistrationsCheck?.hasExportedNotCancelledExamsInCurrentEdexcelSession) ||
      (awardingBodyId === AwardingBodyIdentifiers.AQA &&
        !exportedRegistrationsCheck?.hasExportedNotCancelledExamsInCurrentAqaSession),
    [candidateDetails, exportedRegistrationsCheck, awardingBodyId],
  );

  const registrationPrice = useMemo(() => exams.reduce((sum, item) => sum + item.fee, 0), [exams]);

  const shouldApplyMalaysianTax = useMemo(() => {
    return (
      taxRateData &&
      taxRateData.taxRate > 0 &&
      countryId === MALAYSIA_COUNTRY_ID.toString() &&
      candidateDetails?.isMalaysianCitizen === MalaysianCitizenStatus.No
    );
  }, [taxRateData, countryId, candidateDetails]);

  const taxRateFee = useMemo(() => {
    if (taxRateData) {
      return registrationPrice * taxRateData?.taxRate;
    }
    return 0;
  }, [registrationPrice, taxRateData]);

  const validationSchema = useMemo(() => {
    return getValidation(idTypes, awardingBodyId, eligibilityCheck.proceedWithCashinRequired);
  }, [idTypes, awardingBodyId, eligibilityCheck]);

  const formik = useFormik<CandidateProductDetailsModel>({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      const withCopiedPreviousData = applyPreviousData(values);
      submit(exams, withCopiedPreviousData, candidateDetails?.id);
    },
  });

  function applyPreviousData(values: CandidateProductDetailsModel): CandidateProductDetailsModel {
    return {
      ...values,
      groupAwards: {
        ...values.groupAwards,
        groupAwardExams: values.groupAwards.groupAwardExams.map((x) => ({
          ...x,
          previousCenterNumber: values.previousCentreNumber,
          previousCandidateNumber: values.previousCandidateNumber,
        })),
      },
      examRetake: values.examRetake.map((x) => ({
        ...x,
        previousCandidateNumber: values.previousCandidateNumber,
        previousCenterNumber: values.previousCentreNumber,
      })),
    };
  }

  const fields = useFormFields(formik);
  const mainErrors = useServerErrors(formik, serverErrors);

  useEffect(() => {
    if (candidateDetails && candidateDetails.id) {
      getExportedRegistrationsCheck(candidateDetails.id);
    }
  }, [candidateDetails, getExportedRegistrationsCheck]);

  useEffect(() => {
    const selectedDocumentType = idTypes?.find((x) => x.id === fields.selectedIdentificationDocument.value);
    if (fields.identificationDocumentHasNoExpiryDate.value && selectedDocumentType?.mustHaveExpiryDate) {
      fields.identificationDocumentHasNoExpiryDate.onChange(false);
    }
  }, [fields, idTypes]);

  const groupAwardsIsSelected = useMemo(
    () => formik.values.groupAwards.groupAwardExams.some((x) => x.isSelected === true),
    [formik.values.groupAwards.groupAwardExams],
  );

  const retakeOrCarryForwardExamsSelected = useMemo(() => {
    return exams.some((x) => x.retakeSelected || x.isCarryForward);
  }, [exams]);

  const hasGroupAwards = groupAwardItems && groupAwardItems.length > 0;

  const currency = useMemo(() => exams[0]?.currency ?? '', [exams]);

  const groupAwardsFee = useMemo(
    () =>
      hasGroupAwards
        ? groupAwardItems.reduce(
            (sum, item, i) => (fields.groupAwards.groupAwardExams[i]?.isSelected.value ? sum + item.fee : sum),
            0,
          )
        : 0,
    [groupAwardItems, fields.groupAwards.groupAwardExams],
  );

  const groupAwardsTaxRateFee = useMemo(() => {
    if (taxRateData) {
      return groupAwardsFee * taxRateData?.taxRate;
    }
    return 0;
  }, [groupAwardsFee, taxRateData]);

  return (
    <>
      <MainContainer>
        <h1>Candidate details continued</h1>
      </MainContainer>

      <LoadingContainer loading={isLoading}>
        <PageContainer className="page-container">
          <form onSubmit={formik.handleSubmit}>
            <IdentityDocumentFields fields={fields} disabled={!canEditDocument} idTypes={idTypes} />
            <CandidateExams exams={exams} fields={fields} awardingBodyId={awardingBodyId} canEditUci={canEditUci} />

            {eligibilityCheck.proceedWithCashinRequired && (
              <BcAlert type="note" title="Warning">
                {eligibilityCheck.messages?.map((msg, i) => (
                  <p key={i}>{msg}</p>
                ))}

                <BcCheckbox {...fields.proceedWithCashin}>Do you want to proceed with Cashin?</BcCheckbox>
              </BcAlert>
            )}

            {hasGroupAwards && <GroupAwards fields={fields.groupAwards} groupAwards={groupAwardItems} />}

            {(groupAwardsIsSelected || retakeOrCarryForwardExamsSelected) && (
              <>
                <TextField {...fields.previousCentreNumber} label="Previous Centre Number" showColon={false} />
                <TextField {...fields.previousCandidateNumber} label="Previous Candidate Number" showColon={false} />

                {groupAwardsIsSelected && !retakeOrCarryForwardExamsSelected && (
                  <Quote title="" intent="info">
                    <strong>Note: </strong>Previous registration data is not required for new Candidates
                  </Quote>
                )}
                <Divider />
              </>
            )}

            <PriceSummary>
              {hasGroupAwards && (
                <ReadonlyField label="Group Awards">
                  {groupAwardsFee} {currency}
                </ReadonlyField>
              )}
              {shouldApplyMalaysianTax && (
                <>
                  <ReadonlyField label="Registration Price">
                    {registrationPrice + groupAwardsFee} {currency}
                  </ReadonlyField>
                  <ReadonlyField label={`SST ${taxRateData?.taxRatePercentage}% Tax`}>
                    {Number((taxRateFee + groupAwardsTaxRateFee).toFixed(2))} {currency}
                  </ReadonlyField>
                  <ReadonlyField label="Total Price">
                    {Number((registrationPrice + taxRateFee + groupAwardsFee + groupAwardsTaxRateFee).toFixed(2))}{' '}
                    {currency}
                  </ReadonlyField>
                </>
              )}
              {!shouldApplyMalaysianTax && (
                <>
                  {hasGroupAwards && (
                    <ReadonlyField label="Registration Price">
                      {registrationPrice} {currency}
                    </ReadonlyField>
                  )}
                  <ReadonlyField label="Total Price">
                    {registrationPrice + groupAwardsFee} {currency}
                  </ReadonlyField>
                </>
              )}
            </PriceSummary>
            <OnlinePaymentDiscountAdvertMsg />

            <Divider />

            <FormFooter
              mainErrors={!isSubmitting ? mainErrors : undefined}
              submitCount={formik.submitCount}
              isValid={formik.isValid}
            >
              <Button intent="primary" type="submit" loading={isSubmitting} disabled={isSubmitting}>
                Next
              </Button>
              <Button intent="gray" onClick={goBack} type="button" disabled={isSubmitting}>
                Back
              </Button>
            </FormFooter>
          </form>
        </PageContainer>
      </LoadingContainer>
    </>
  );
};

const PriceSummary = styled.div`
  text-align: right;

  .form-group {
    margin-bottom: 0;
  }

  strong {
    display: inline-block;
    min-width: 130px;
  }
`;
